import { useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';

const useSessionTimeout = (options = {}) => {
  const { oktaAuth } = useOktaAuth();
  const {
    idleTimeout = 30 * 60 * 1000,      // 30 minutes of inactivity
    sessionTimeout = 8 * 60 * 60 * 1000, // 8 hours total session
    warningTime = 5 * 60 * 1000,       // 5 minutes warning before logout
    checkInterval = 60 * 1000          // Check every minute
  } = options;

  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  const lastActivityRef = useRef(Date.now());
  const sessionStartRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const checkTimerRef = useRef(null);

  // Reset activity timer
  const resetActivity = () => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  };

  // Logout user
  const logout = () => {
    console.log('Session timeout - logging out user');
    setShowWarning(false);
    oktaAuth.signOut();
  };

  // Extend session (reset timers)
  const extendSession = () => {
    resetActivity();
    sessionStartRef.current = Date.now();
    console.log('Session extended by user');
  };

  // Check for timeouts
  const checkTimeouts = () => {
    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const timeSinceSessionStart = now - sessionStartRef.current;

    // Check session timeout (absolute maximum)
    if (timeSinceSessionStart > sessionTimeout) {
      console.log('Maximum session time reached');
      logout();
      return;
    }

    // Check idle timeout
    if (timeSinceActivity > idleTimeout) {
      console.log('Idle timeout reached');
      logout();
      return;
    }

    // Check if we should show warning
    const timeUntilIdleTimeout = idleTimeout - timeSinceActivity;
    const timeUntilSessionTimeout = sessionTimeout - timeSinceSessionStart;
    const timeUntilTimeout = Math.min(timeUntilIdleTimeout, timeUntilSessionTimeout);

    if (timeUntilTimeout <= warningTime && !showWarning) {
      console.log('Showing session timeout warning');
      setShowWarning(true);
      setTimeRemaining(Math.ceil(timeUntilTimeout / 1000));
      
      // Start countdown timer
      warningTimerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            logout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  // Activity event handlers
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  useEffect(() => {
    // Add activity listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, resetActivity, true);
    });

    // Start timeout checker
    checkTimerRef.current = setInterval(checkTimeouts, checkInterval);

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetActivity, true);
      });
      
      if (checkTimerRef.current) {
        clearInterval(checkTimerRef.current);
      }
      
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current);
      }
    };
  }, []);

  return {
    showWarning,
    timeRemaining,
    extendSession,
    logout
  };
};

export default useSessionTimeout;